import * as React from 'react'

import type { Popover } from '@base-ui/react/popover'

// For a popover whose anchor holds more than its trigger (a pill with paging
// arrows, a field with removable chips): a press on the anchor, or focus moving
// into it (the trigger's focus-out), is not leaving the popup.
export function isWithinAnchor(
  details: Popover.Root.ChangeEventDetails,
  anchor: React.RefObject<HTMLElement>,
): boolean {
  const destination =
    details.reason === 'focus-out'
      ? (details.event as FocusEvent).relatedTarget
      : details.reason === 'outside-press'
        ? details.event.target
        : null
  return destination instanceof Node && !!anchor.current?.contains(destination)
}

// The other half of isWithinAnchor. Base UI only closes on focus leaving the
// trigger or the popup, so once focus rests on another control in the anchor,
// leaving from there has to close the popup here. The open trigger names its
// popup through `aria-controls`. Tab lands on Base UI's focus guards first, which
// route it into the popup, so reaching a guard is not leaving.
export function useCloseOnFocusLeavingAnchor(
  anchor: React.RefObject<HTMLElement>,
  isOpen: boolean,
  onClose: () => void,
): void {
  React.useEffect(() => {
    const node = anchor.current
    if (!isOpen || !node) {
      return undefined
    }
    const handleFocusOut = (event: FocusEvent) => {
      const trigger = node.querySelector('[aria-controls]')
      if (!trigger || event.target === trigger) {
        return
      }
      const next = event.relatedTarget
      const popup = document.getElementById(
        trigger.getAttribute('aria-controls'),
      )
      if (
        next instanceof Element &&
        (node.contains(next) ||
          popup?.contains(next) ||
          next.hasAttribute('data-base-ui-focus-guard'))
      ) {
        return
      }
      if (trigger.getAttribute('aria-expanded') === 'true') {
        onClose()
      }
    }
    node.addEventListener('focusout', handleFocusOut)
    return () => node.removeEventListener('focusout', handleFocusOut)
  }, [anchor, isOpen, onClose])
}
